import React, { useState, useEffect, useRef } from 'react';

const AddSupportDocumentModal = ({ isOpen, onClose, onAdd, editingDocument }) => {
  const [documentName, setDocumentName] = useState('');
  const [documentDetails, setDocumentDetails] = useState('');
  const [fee, setFee] = useState('');
  const [feeType, setFeeType] = useState('free'); // 'free' or 'paid'
  const [isLocked, setIsLocked] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const existingFileUrl = editingDocument?.fileUrl || editingDocument?.url || '';

  // Load document data when editing
  useEffect(() => {
    if (editingDocument) {
      setDocumentName(editingDocument.name || '');
      setDocumentDetails(editingDocument.detail || '');
      const docFee = editingDocument.fee || '£0.00';
      if (docFee === '£0.00') {
        setFeeType('free');
        setFee('');
      } else {
        setFeeType('paid');
        setFee(docFee);
      }
      setIsLocked(editingDocument.isLocked || false);
      setSelectedFile(null);
    } else {
      // Reset form when not editing
      setDocumentName('');
      setDocumentDetails('');
      setFee('');
      setFeeType('free');
      setIsLocked(false);
      setSelectedFile(null);
    }
  }, [editingDocument]);

  const handleSubmit = () => {
    if (!documentName.trim()) {
      alert('Please enter a document name');
      return;
    }

    // Parse fee value
    const feeValue = feeType === 'paid' && fee ? parseFloat(fee.replace(/£/g, '').replace(/,/g, '')) || 0 : 0;
    // Use checkbox value - admin can lock even free documents
    // If paid and fee > 0, default to locked unless explicitly unchecked
    const shouldLock = feeValue > 0 ? (isLocked !== false) : isLocked;
    
    const newDocument = {
      name: documentName,
      detail: documentDetails,
      fee: feeType === 'free' ? '£0.00' : (fee || '£0.00'),
      isLocked: shouldLock,
      file: selectedFile,
      fileUrl: existingFileUrl
    };

    onAdd(newDocument);
    
    // Reset form
    setDocumentName('');
    setDocumentDetails('');
    setFee('');
    setFeeType('free');
    setIsLocked(false);
    setSelectedFile(null);
    onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const next = e.relatedTarget;
    if (next && e.currentTarget.contains(next)) {
      return;
    }
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      e.dataTransfer.dropEffect = 'copy';
    } catch {
      /* ignore */
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Modal Content */}
        <div className="p-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-2xl font-semibold text-gray-900">
              {editingDocument ? 'Edit Support Document' : 'Add Support Document'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* Form Content */}
          <div className="space-y-8">
            {/* Document Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Name
              </label>
              <input
                type="text"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition-all"
                placeholder=""
              />
            </div>

            {/* Document Details */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Details
              </label>
              <textarea
                rows={4}
                value={documentDetails}
                onChange={(e) => setDocumentDetails(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition-all resize-none"
                placeholder=""
              />
            </div>

            {/* Fee Type Radio Buttons */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Fee Type
              </label>
              <div className="flex gap-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="feeType"
                    value="free"
                    checked={feeType === 'free'}
                    onChange={(e) => setFeeType(e.target.value)}
                    className="w-5 h-5 text-emerald-400 border-gray-300 focus:ring-emerald-400 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700 font-medium">Free</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="feeType"
                    value="paid"
                    checked={feeType === 'paid'}
                    onChange={(e) => setFeeType(e.target.value)}
                    className="w-5 h-5 text-emerald-400 border-gray-300 focus:ring-emerald-400 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700 font-medium">Paid</span>
                </label>
              </div>
            </div>

            {/* Fee Input - Only show when Paid is selected */}
            {feeType === 'paid' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fee Amount
                </label>
                <input
                  type="text"
                  value={fee}
                  onChange={(e) => {
                    setFee(e.target.value);
                    // Auto-lock if fee is set
                    const feeValue = parseFloat(e.target.value.replace(/£/g, '').replace(/,/g, '')) || 0;
                    if (feeValue > 0) {
                      setIsLocked(true);
                    }
                  }}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition-all"
                  placeholder="e.g., £50.00"
                />
              </div>
            )}

            {/* Lock Checkbox - Show for both free and paid */}
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isLocked}
                  onChange={(e) => setIsLocked(e.target.checked)}
                  className="w-5 h-5 text-emerald-400 border-gray-300 rounded focus:ring-2 focus:ring-emerald-400 cursor-pointer"
                />
                <span className="text-sm font-medium text-gray-700">
                  Lock {feeType === 'paid' ? '(Requires payment to access)' : '(Restrict access)'}
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-8">
                {feeType === 'paid' 
                  ? 'Locked documents are only accessible after payment is completed'
                  : 'Locked documents require admin approval or payment to access'}
              </p>
            </div>

            {/* File Upload Area */}
            <div>
              <div
                className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer ${
                  isDragging
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-emerald-400 hover:border-emerald-500 bg-white'
                }`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  id="file-upload"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 15V9M12 9L9 12M12 9L15 12" stroke="#40C191" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 17L3.879 19.879C4.01799 20.3821 4.31451 20.8248 4.72383 21.1405C5.13315 21.4561 5.63382 21.6284 6.14806 21.6316H17.852C18.3662 21.6284 18.8668 21.4561 19.2762 21.1405C19.6855 20.8248 19.982 20.3821 20.121 19.879L21 17" stroke="#40C191" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-emerald-500 font-medium text-base mb-1">Drag & Drop</p>
                    {selectedFile ? (
                      <p className="text-sm text-gray-600 mt-2">
                        Selected: {selectedFile.name}
                      </p>
                    ) : existingFileUrl ? (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Already uploaded</p>
                        <a
                          href={existingFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-emerald-600 font-medium hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View current document
                        </a>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-10">
            <button
              onClick={onClose}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-6 py-2.5 bg-emerald-400 text-white text-sm font-medium rounded-lg hover:bg-emerald-500 transition-colors shadow-sm"
            >
              {editingDocument ? 'Update Document' : 'Add Document'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSupportDocumentModal;

